/* ns-cart-upsell.js
   - Monteert het upsellblok in de cart drawer (tussen items en summary)
   - Voegt upsell via /cart/add.js toe zonder redirect
   - Ververst header, summary én ITEMS-HTML zodat je toevoeging direct zichtbaar is
*/
(() => {
  // ---- helpers ------------------------------------------------------------
  const Q = {
    drawers() { return Array.from(document.querySelectorAll('cart-drawer-component, cart-drawer')); },
    content(d) { return d.querySelector('.cart-drawer__content') || d; },
    // Ondersteunt o.a. <scroll-hint class="cart-drawer__items">
    items(d)   { return d.querySelector('.cart-drawer__items, scroll-hint.cart-drawer__items'); },
    summary(d) { return d.querySelector('.cart-drawer__summary'); },
    header(d)  { return d.querySelector('#cart-drawer-header') || d.querySelector('.cart-drawer__header'); },
    hiddenWrapper() { return document.querySelector('#cart-drawer-upsell-wrapper'); }
  };

  const NSM = { isMobile() { return window.matchMedia('(max-width: 749px)').matches; } };

  // Forceer verse HTML (geen CDN/browse cache)
  function freshURL(url = window.location.href) {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}ns-cache=${Date.now()}`;
  }

  // Minimal DOM-diff voor upsellblok
  function reconcileUpsell(existingBlock, nextBlock) {
    if (!existingBlock || !nextBlock) return;

    // 1) classes / inline style
    existingBlock.className = nextBlock.className;
    const nextStyle = nextBlock.getAttribute('style');
    if (nextStyle != null) existingBlock.setAttribute('style', nextStyle);
    else existingBlock.removeAttribute('style');

    // 2) titel
    const exTitle = existingBlock.querySelector('.cart-drawer-upsell-title');
    const nxTitle = nextBlock.querySelector('.cart-drawer-upsell-title');
    if (exTitle && nxTitle && exTitle.innerHTML !== nxTitle.innerHTML) {
      exTitle.innerHTML = nxTitle.innerHTML;
    }

    // 3) lijst diffen
    const exUL = existingBlock.querySelector('.cart-drawer-upsell-list');
    const nxUL = nextBlock.querySelector('.cart-drawer-upsell-list');
    if (!exUL || !nxUL) return;

    exUL.dataset.listStyleCartDrawer = nxUL.dataset.listStyleCartDrawer || '';
    exUL.dataset.listStyleCartPage   = nxUL.dataset.listStyleCartPage || '';
    exUL.dataset.per                 = nxUL.dataset.per || '';

    const exArr = Array.from(exUL.querySelectorAll('li[data-product-id]'));
    const nxArr = Array.from(nxUL.querySelectorAll('li[data-product-id]'));
    const exMap = new Map(exArr.map(li => [li.dataset.productId, li]));
    const nxMap = new Map(nxArr.map(li => [li.dataset.productId, li]));

    // verwijderen
    exArr.forEach(li => { if (!nxMap.has(li.dataset.productId)) li.remove(); });

    // toevoegen in juiste volgorde
    nxArr.forEach((nli, idx) => {
      const id = nli.dataset.productId;
      if (exMap.has(id)) return;
      let insertBeforeEl = null;
      for (let j = idx + 1; j < nxArr.length; j++) {
        const sibId = nxArr[j].dataset.productId;
        const found = exUL.querySelector(`li[data-product-id="${sibId.replace(/"/g, '\\"')}"]`);
        if (found) { insertBeforeEl = found; break; }
      }
      exUL.insertBefore(nli, insertBeforeEl);
    });

    // leeg? verwijder blok
    if (!exUL.querySelector('li[data-product-id]')) {
      existingBlock.remove();
    }
  }

  // ---- Mount: ALTIJD sibling tussen items en summary ----
  function mountInDrawer(drawer, freshWrapper) {
    const content = Q.content(drawer);
    const items   = Q.items(drawer);
    const summary = Q.summary(drawer);
    if (!content || !items || !summary) return;

    const src = freshWrapper || Q.hiddenWrapper();
    if (!src) return;

    const tmp = document.createElement('div');
    tmp.innerHTML = src.innerHTML.trim();
    const nextBlock = tmp.querySelector('#cart-drawer-upsell');

    // Kan (nog) in content of (van eerdere versies) in summary staan
    let existingBlock =
      content.querySelector('#cart-drawer-upsell') ||
      summary.querySelector('#cart-drawer-upsell');

    // Geen items in next -> verwijder evt. bestaande en klaar
    if (!nextBlock || !nextBlock.querySelector('.cart-drawer-upsell-list li')) {
      existingBlock?.remove();
      return;
    }

    // Doelpositie: direct vóór summary (dus ónder items)
    const wantParent = summary.parentNode;
    const wantNext   = summary;

    if (!existingBlock) {
      wantParent.insertBefore(nextBlock, wantNext);
      return;
    }

    // Staat hij in summary of op de verkeerde plek? verplaats
    if (existingBlock.parentNode !== wantParent || existingBlock.nextElementSibling !== wantNext) {
      existingBlock.remove();
      wantParent.insertBefore(nextBlock, wantNext);
      return;
    }

    // Zelfde plek: alleen diffen
    reconcileUpsell(existingBlock, nextBlock);
  }

  function rehydrateWrapperAndMountAll() {
    fetch(freshURL(), { credentials: 'same-origin', cache: 'no-store' })
      .then(r => r.text())
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('#cart-drawer-upsell-wrapper');
        const old = Q.hiddenWrapper();
        if (fresh && old) old.replaceWith(fresh);
        Q.drawers().forEach(d => mountInDrawer(d, fresh));
      })
      .catch(() => {});
  }

  // Cart count voor empty-state normalisatie (alleen mobiel, extra veilig)
  function getCartCount() {
    return fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(r => r.json())
      .then(c => c?.item_count || 0)
      .catch(() => 0);
  }

  // Ververs drawer NA add/remove:
  // ⚠️ Items-componenten in themes luisteren niet altijd naar events,
  // daarom updaten we hier OOK de items-HTML (innerHTML) zodat je
  // toevoeging onmiddellijk zichtbaar is.
  function refreshCartDrawerUI() {
    return fetch(freshURL(), { credentials: 'same-origin', cache: 'no-store' })
      .then(r => r.text())
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');

        Q.drawers().forEach(drawer => {
          const curSummary = Q.summary(drawer);
          const curHeader  = Q.header(drawer);
          const curItems   = Q.items(drawer);

          const newSummary = doc.querySelector('.cart-drawer__summary');
          const newHeader  = doc.querySelector('#cart-drawer-header, .cart-drawer__header');
          const newItems   = doc.querySelector('.cart-drawer__items, scroll-hint.cart-drawer__items');

          // 1) Summary + header bijwerken
          if (curSummary && newSummary) curSummary.innerHTML = newSummary.innerHTML;
          if (curHeader && newHeader)   curHeader.innerHTML  = newHeader.innerHTML;

          // 2) Items bijwerken (alleen de binnenkant; laat het custom element bestaan)
          if (curItems && newItems) {
            const prevScroll = curItems.scrollTop;
            curItems.innerHTML = newItems.innerHTML;
            curItems.scrollTop = prevScroll;

            // Sommige themes binden listeners tijdens connectCallback; na innerHTML verversen
            // opnieuw events laten “prikkelen”
            curItems.dispatchEvent(new Event('change', { bubbles: true }));
            curItems.dispatchEvent(new Event('input', { bubbles: true }));
          }
        });

        // Upsell op juiste plek diff-mounten
        rehydrateWrapperAndMountAll();

        // Alleen mobiel: empty-state classes normaliseren (extra guard)
        if (NSM.isMobile()) {
          return getCartCount().then(count => {
            const empty = count === 0;
            Q.drawers().forEach(drawer => {
              const root = drawer;
              const content = Q.content(drawer) || drawer;
              ['is-empty', 'cart--empty', 'drawer--empty'].forEach(cls => {
                root.classList.toggle(cls, empty);
                content.classList.toggle(cls, empty);
              });
            });
          });
        }
      });
  }

  function observeDrawers() {
    Q.drawers().forEach(drawer => {
      const ob = new MutationObserver(() => {
        clearTimeout(drawer._nsUpsellTimer);
        drawer._nsUpsellTimer = setTimeout(rehydrateWrapperAndMountAll, 120);
      });
      ob.observe(drawer, { childList: true, subtree: true, attributes: true });
    });

    document.addEventListener('cart-drawer-component:open', rehydrateWrapperAndMountAll);
    window.addEventListener('cart:updated', rehydrateWrapperAndMountAll);
    window.addEventListener('cart:change', rehydrateWrapperAndMountAll);
  }

  // publiek: variant image swap bij select change
  window.onChangeNsUpsellVariantSelector = function(select, event) {
    if (event) event.preventDefault();
    const imgSrc = select.options[select.selectedIndex]?.dataset?.variantImg;
    if (!imgSrc) return;
    const img = select.closest('.cart-drawer-upsell-list-item')?.querySelector('.cart-drawer-upsell-list-item-left img');
    if (img) img.src = imgSrc;
  };

  // AJAX submit voor upsell formulieren (voorkomt redirect + ververst UI)
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('#cart-drawer-upsell form');
    if (!form) return;

    e.preventDefault();

    const btn = form.querySelector('button[name="add"]');
    const prevDisabled = btn?.disabled;
    if (btn) btn.disabled = true;

    const fd = new FormData(form);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'Cache-Control': 'no-store' },
      body: fd,
      cache: 'no-store'
    })
      .then(res => res.ok ? res.json() : Promise.reject(res))
      .then(() => refreshCartDrawerUI()) // summary/header + ITEMS updaten + upsell mounten
      .then(() => {
        // Breed dispatchen voor theme-compat
        document.dispatchEvent(new CustomEvent('ns:cart:shouldSync', { detail: { source: 'upsell-add' }}));
        window.dispatchEvent(new Event('cart:updated'));
        window.dispatchEvent(new Event('cart:refresh'));
        // Extra veelvoorkomende custom events van themes
        window.dispatchEvent(new Event('theme:cart:change'));
        document.dispatchEvent(new Event('cart:request-end'));
      })
      .catch(() => { /* optioneel: error tonen */ })
      .finally(() => { if (btn) btn.disabled = !!prevDisabled; });
  });

  // ---- boot ----------------------------------------------------------------
  rehydrateWrapperAndMountAll();
  observeDrawers();
})();
